/**
 * 67.55: how a vehicle is heard (the owner, Oct 18: "vehicle sounds"). The rules only; `vehicleSound.ts` voices them.
 *
 * Each driving body has an engine and a running gear:
 * - **the engine** — at idle with a driver in its seat (silent with none); its revs, gear and load from a drivetrain
 *   (67.67, `drivetrain.ts`: the gears' ratios, shifts with a transition, the load from the throttle and the grade —
 *   up a hill it kicks down and works, down one it runs on the wheels); brighter and rougher under load than coasting;
 *   a gas bike revs high and buzzy, the technical and the supply truck are petrol trucks, the 6×6 a diesel (low, square,
 *   a clatter in it), the M113 a two-stroke diesel (67.67: a howl over the clatter and its blower's whine); the electric
 *   bike only whines, and quietly — one gear, a deeper hum under load, regen downhill;
 * - **the running gear** — the tyres' (or the tracks') noise on the ground under them by its material (soil a dull
 *   rumble, rock a gritty crunch, sand a soft hiss, snow a squeak, ice a thin hiss, mud a slow squelch, ash a soft
 *   rumble, salt a light crunch), louder with speed; the tracks' links clank at a rate their speed sets and squeal on
 *   their sprockets and idlers, most turning (67.67: on the spot too).
 *
 * Heard by distance as the shots are (`gunfireMix.hearShot`): quieter and darker with distance, from its side; each body
 * its own reach (a diesel carries further than a bike). From above (68.14), a vehicle past the far band is no voice of
 * its own but a share of the far rumble's energy (`vehicleRumbleRate`).
 *
 * Pure: no audio here, no game state written.
 */
import type { GameState, GroundMaterial, StructureState, StructureType, Vec3 } from '../types';
import { ABOVE, STONE_VOICE, ICE_VOICE, type StoneVoice } from './gunfireMix';
import { bikeNumbers, driveSpec } from '../core/drive';
import { groundMaterial } from '../core/groundMaterial';
import { personSetOf } from '../core/scaleProfile';
import { personSurfaceRadius } from '../core/fineTerrain';
import { getSurfaceRadius } from '../core/surface';
import { vecAdd, vecNormalize, vecScale, vecDot } from '../math';
import { PERSON_BODY_HEIGHT } from '../constants';
import { GEARBOXES, settledDrivetrain, type DriveInput, type DrivetrainState } from './drivetrain';

export type VehicleVoiceKind = 'bikeGas' | 'bikeElectric' | 'technical' | 'truck' | 'transport' | 'tracked';
export const VEHICLE_VOICE_KINDS: VehicleVoiceKind[] = ['bikeGas', 'bikeElectric', 'technical', 'truck', 'transport', 'tracked'];

/**
 * An engine's voice: its firing pitch at idle and at the redline (Hz; its gears are drivetrain.ts GEARBOXES), the
 * oscillator its body is, the sub an octave under (0–1), the roughness (0–1: a diesel's clatter), the intake and exhaust
 * noise (0–1) and its band (Hz), its loudness, how far it is heard and how fast it falls off (people). `electric`: a motor's whine, no firing;
 * `tracks`: links that clank and squeal; `blower` (67.67): a Roots blower's whine at this multiple of the firing pitch
 * (the M113's two-stroke).
 */
export interface EngineSpec {
  name: string;
  idle: number; top: number;
  body: OscillatorType; sub: number; rough: number; noise: number; noiseHz: number;
  gain: number; range: number; reach: number;
  roll: number;
  electric?: boolean; tracks?: boolean; blower?: number;
}
export const ENGINES: Record<VehicleVoiceKind, EngineSpec> = {
  bikeGas: { name: 'motorcycle (gas)', idle: 42, top: 150, body: 'sawtooth', sub: 0.25, rough: 0.35, noise: 0.3, noiseHz: 1600, gain: 0.45, range: 160, reach: 10, roll: 0.25 },
  bikeElectric: { name: 'motorcycle (electric)', idle: 0, top: 1500, body: 'sine', sub: 0, rough: 0, noise: 0.1, noiseHz: 3000, gain: 0.16, range: 45, reach: 4, roll: 0.3, electric: true },
  technical: { name: 'technical (petrol)', idle: 30, top: 100, body: 'sawtooth', sub: 0.4, rough: 0.25, noise: 0.3, noiseHz: 900, gain: 0.55, range: 200, reach: 14, roll: 0.4 },
  truck: { name: 'supply truck (petrol)', idle: 26, top: 84, body: 'sawtooth', sub: 0.5, rough: 0.3, noise: 0.3, noiseHz: 800, gain: 0.6, range: 220, reach: 16, roll: 0.45 },
  transport: { name: '6×6 transport (diesel)', idle: 21, top: 64, body: 'square', sub: 0.6, rough: 0.6, noise: 0.35, noiseHz: 600, gain: 0.7, range: 300, reach: 20, roll: 0.5 },
  // 67.67 (the owner: a better M113): the Detroit 6V53 two-stroke — a higher howl than the 6×6 over a harder clatter,
  // its blower whining over it, the tracks clanking and squealing. Louder than the 6×6 by its character, not its gain
  // (the owner: "don't want to mess with volume too much"): its peak held at the 6×6's
  tracked: { name: 'M113 carrier (two-stroke diesel)', idle: 27, top: 96, body: 'square', sub: 0.7, rough: 0.8, noise: 0.45, noiseHz: 520, gain: 0.6, range: 320, reach: 22, roll: 0.4, tracks: true, blower: 9.5 },
};

/** Which voice a driving body has (absent: it does not drive). */
export function vehicleVoiceOf(type: StructureType | 'tracked', variant?: 'gas' | 'electric'): VehicleVoiceKind | null {   // ('tracked': the carrier, not a body in play yet)
  if (type === 'motorcycle') return variant === 'electric' ? 'bikeElectric' : 'bikeGas';
  if (type === 'technical') return 'technical';
  if (type === 'supplyTruck') return 'truck';
  if (type === 'wheeled') return 'transport';
  if (type === 'tracked' || type === 'tank') return 'tracked';   // (69.29: the tank on the carrier's tracks and engine, for now)
  return null;
}

/**
 * The running gear's noise on each ground: the filter (type, Hz, Q), its grit (an amplitude flutter, Hz at full speed) and
 * loudness; 67.80: how much of the grit is random rather than a steady wave (`chatter`, 0 … 1; absent 0) and the gravel's
 * crunch in it (`crunch`, its level; absent none).
 */
export interface RollSpec { type: BiquadFilterType; hz: number; q: number; grit: number; gain: number; chatter?: number; crunch?: number }
export const ROLL: Record<GroundMaterial, RollSpec> = {
  soil: { type: 'lowpass', hz: 420, q: 0.7, grit: 9, gain: 0.8 },
  // 67.80 (the owner: "driving wheeled vehicles on stone sounds weird"): a tyre on hard ground is a broad, lower roar, its
  // grit random (the stones under it, not a wave) and gravel crunching in it — before, a 1.7 kHz band fluttered by a steady
  // 31 Hz triangle, a buzz (ROLL_BEFORE_ROCK)
  rock: { type: 'bandpass', hz: 650, q: 0.5, grit: 24, gain: 0.75, chatter: 1, crunch: 0.55 },
  sand: { type: 'lowpass', hz: 1300, q: 0.5, grit: 4, gain: 0.55 },
  snow: { type: 'bandpass', hz: 2400, q: 2.5, grit: 13, gain: 0.35 },
  // 67.80, ice (the owner: "it wasn't rock but ice, it's just sort of annoying"): a tyre on ice is a soft, broad skid hiss,
  // its texture random, a little ice crackling — before, everything above 2.8 kHz whooshing at a steady 2 Hz (ROLL_BEFORE_ICE)
  ice: { type: 'bandpass', hz: 1100, q: 0.4, grit: 8, gain: 0.3, chatter: 1, crunch: 0.12 },
  mud: { type: 'lowpass', hz: 300, q: 1.6, grit: 3, gain: 0.9 },
  ash: { type: 'lowpass', hz: 650, q: 0.6, grit: 6, gain: 0.6 },
  salt: { type: 'bandpass', hz: 2100, q: 1.1, grit: 24, gain: 0.5 },
};
/** 67.80: stone's and ice's running gear before (the sheet's A). */
export const ROLL_BEFORE_ROCK: RollSpec = { type: 'bandpass', hz: 1700, q: 0.9, grit: 31, gain: 0.7 };
export const ROLL_BEFORE_ICE: RollSpec = { type: 'highpass', hz: 2800, q: 0.6, grit: 2, gain: 0.3 };
/** The running gear's spec on `material`, stone and ice by their voices. */
export function rollSpec(material: GroundMaterial, stone: StoneVoice = STONE_VOICE, ice: StoneVoice = ICE_VOICE): RollSpec {
  if (material === 'rock' && stone === 'before') return ROLL_BEFORE_ROCK;
  if (material === 'ice' && ice === 'before') return ROLL_BEFORE_ICE;
  return ROLL[material] ?? ROLL.soil;
}

const clamp = (x: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, x));

/**
 * A vehicle's state, as the sound reads it: a driver in its seat, its speed's share of its top speed, its throttle
 * (−1 … 1), the ground's material under it; 67.67: the grade along its heading (+ up), its turn (the yaw rate's share of
 * its turn on the spot), backing up.
 */
export interface VehicleState { running: boolean; speedFrac: number; throttle: number; material: GroundMaterial; grade?: number; turn?: number; reverse?: boolean; neutral?: boolean }

/** What the drivetrain reads from a vehicle's state (`drivetrain.ts`). */
export function driveInputOf(s: VehicleState): DriveInput {
  return { running: s.running, speedFrac: Math.abs(s.speedFrac), throttle: s.throttle, grade: s.grade ?? 0, turn: s.turn ?? 0, reverse: !!s.reverse, neutral: s.neutral };
}

/**
 * How a vehicle is heard at the listener: its engine's pitch, load and loudness (and `near`, the distance's share of it),
 * the running gear's, the tracks' clank and squeal, the air's cut, its side; 67.67: `drive`, what its drivetrain reads (a
 * voice steps its own: vehicleSound.ts), and `train`, where a steady drive would sit.
 */
export interface VehicleHeard {
  engine: { gain: number; pitch: number; load: number; near: number };
  roll: { gain: number; type: BiquadFilterType; hz: number; q: number; grit: number; /** 67.80: the grit's random share, and the gravel's crunch (its level, its rate in pops' Hz) */ chatter: number; crunch: number; crunchHz: number };
  clatter: { gain: number; rate: number; squeal: number };
  cutoff: number;
  pan: number;
  drive: DriveInput;
  train: DrivetrainState;
}

/**
 * The engine as heard, from its drivetrain (67.67): the pitch from the revs (a touch higher under load); louder and
 * brighter under load; on the wheels (negative load) a little quieter. Electric: the whine follows the motor (the
 * wheels'), louder with the load either way (driving, or regen downhill).
 */
export function engineHeard(kind: VehicleVoiceKind, near: number, running: boolean, t: Pick<DrivetrainState, 'rev' | 'load'>): { gain: number; pitch: number; load: number } {
  const e = ENGINES[kind], rev = clamp(t.rev, 0, 1.1), push = Math.max(0, t.load);
  if (!running) return { gain: 0, pitch: e.electric ? e.top * 0.15 : e.idle, load: 0 };
  if (e.electric) return { gain: e.gain * near * (0.5 + 0.5 * Math.min(1, Math.abs(t.load) + 0.2 * rev)), pitch: e.top * (0.15 + 0.85 * rev), load: t.load };
  return { gain: e.gain * near * (0.6 + 0.4 * push) * (t.load < 0 ? 0.85 : 1), pitch: e.idle + (e.top - e.idle) * rev * (1 + 0.04 * push), load: t.load };
}

/** A vehicle `people` from the listener, on its `pan` side (−1 … 1). Null when silent (nobody driving, not rolling) or past its range. */
export function hearVehicle(people: number, pan: number, kind: VehicleVoiceKind, s: VehicleState, stone: StoneVoice = STONE_VOICE, ice: StoneVoice = ICE_VOICE): VehicleHeard | null {
  const e = ENGINES[kind], speed = clamp(Math.abs(s.speedFrac), 0, 1);
  if (people > e.range || (!s.running && speed < 0.02)) return null;
  const near = 1 / (1 + Math.max(0, people) / e.reach);
  const drive = driveInputOf(s), train = settledDrivetrain(GEARBOXES[kind], drive);
  const r = rollSpec(s.material, stone, ice);
  // the tracks run turning on the spot too (counter-rotating); they squeal most slow and turning
  const turn = clamp(drive.turn, 0, 1), links = Math.min(1, Math.max(speed, 0.5 * turn));
  return {
    engine: { ...engineHeard(kind, near, s.running, train), near },
    roll: { gain: e.roll * r.gain * Math.pow(speed, 0.8) * near, type: r.type, hz: r.hz, q: r.q, grit: r.grit * (0.3 + 0.7 * speed),
      chatter: r.chatter ?? 0, crunch: (r.crunch ?? 0) * e.roll * Math.pow(speed, 0.7) * near, crunchHz: 10 + 50 * speed },
    clatter: e.tracks
      ? { gain: 0.5 * Math.pow(links, 0.7) * near, rate: 6 + 30 * links, squeal: (0.1 * Math.min(1, links * 3) * (1 - 0.5 * Math.min(1, speed)) + 0.3 * turn) * near }
      : { gain: 0, rate: 0, squeal: 0 },
    cutoff: clamp(9000 * Math.exp(-Math.max(0, people) / 110), 280, 9000),
    pan: clamp(pan, -1, 1),
    drive, train,
  };
}

/**
 * A structure as the sound reads it (null: it does not drive, or is destroyed): its voice, a driver in its driver's seat
 * (the engine running), its speed's share of its build's top, its last throttle, the ground's material under it at
 * `actionSeconds` into the match; 67.67: the grade along its heading (read as core/drive.ts reads it, the ground half a
 * wheelbase ahead and behind — two ground lookups, only for a body running or rolling), its turn, backing up.
 */
export function vehicleStateOf(s: StructureState, state: Pick<GameState, 'config' | 'turnNumber' | 'planets'>, actionSeconds: number): { kind: VehicleVoiceKind; vs: VehicleState } | null {
  if (!s.drive || s.health <= 0) return null;
  const kind = vehicleVoiceOf(s.type, s.drive.variant);
  if (!kind) return null;
  const top = bikeNumbers(s.drive.variant, personSetOf(state as GameState).walkSpeed, driveSpec(s.type)).top;
  const running = !!s.seats?.some(seat => seat.role === 'driver' && seat.occupant !== null);
  const planet = state.planets[s.planetIndex];
  const material: GroundMaterial = planet ? groundMaterial(state, s.planetIndex, s.surfaceDir, actionSeconds) : 'soil';
  const spec = driveSpec(s.type), speed = s.drive.speed;
  const grade = planet && (running || Math.abs(speed) > 1e-6) ? gradeAlong(planet, s.surfaceDir, s.drive.heading, spec.wheelbase, personSetOf(state as GameState)) : 0;
  const turn = spec.pivotRate ? clamp(Math.abs(s.drive.yawRate ?? 0) / spec.pivotRate, 0, 1) : 0;
  return { kind, vs: { running, speedFrac: top > 0 ? Math.abs(speed) / top : 0, throttle: s.drive.throttle ?? 0, material, grade, turn, reverse: speed < 0 } };
}

/** The ground's rise over run along `heading` (+ up) over half a wheelbase (people) each way — core/drive.ts's own reading of the hill. */
export function gradeAlong(planet: GameState['planets'][number], surfaceDir: Vec3, heading: Vec3, wheelbase: number, ps: ReturnType<typeof personSetOf>): number {
  const n = vecNormalize(surfaceDir), h0 = vecAdd(heading, vecScale(n, -vecDot(heading, n))), hl = Math.hypot(h0.x, h0.y, h0.z);
  if (hl < 1e-9) return 0;
  const h = vecScale(h0, 1 / hl), probe = wheelbase * 0.5 * ps.k * PERSON_BODY_HEIGHT, a = probe / planet.radius;
  const ground = (dir: Vec3) => (ps.fineAmplitude > 0 ? personSurfaceRadius(planet, dir, ps) : getSurfaceRadius(planet, dir));
  const along = (sgn: number) => vecNormalize(vecAdd(vecScale(n, Math.cos(a)), vecScale(h, sgn * Math.sin(a))));
  return clamp((ground(along(1)) - ground(along(-1))) / (2 * probe), -1, 1);
}

/** At most this many vehicles have a voice at once: the loudest. */
export const VEHICLE_VOICES = 6;

/**
 * From above (68.14): a vehicle `cameraPeople` from the camera past the far band is not voiced but feeds the far rumble —
 * this much of its energy a second (a diesel more than a bike, more when moving). The host adds it each frame × dt.
 */
export function vehicleRumbleRate(cameraPeople: number, kind: VehicleVoiceKind, s: VehicleState): number {
  if (cameraPeople <= ABOVE.farBand || (!s.running && Math.abs(s.speedFrac) < 0.02)) return 0;
  const e = ENGINES[kind];
  return 0.6 * ABOVE.rumbleShare * (e.gain / 0.75) * (0.3 + 0.7 * clamp(Math.abs(s.speedFrac), 0, 1)) / (1 + cameraPeople / ABOVE.rumbleReach);
}
/** From above: is this vehicle past the far band (folded into the rumble rather than voiced)? */
export function vehicleFoldsIntoRumble(cameraPeople: number): boolean { return cameraPeople > ABOVE.farBand; }
